import { useState, useEffect } from 'react';
import { Send } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { findItems, addItem } from '@/utils/localStore';

export default function CommentSection({ postId, commentsCount, expanded, onCommentAdd }) {
  const { user } = useAuth();
  const [comments, setComments] = useState([]);
  const [input, setInput] = useState('');
  const [likedComments, setLikedComments] = useState({});
  const [loaded, setLoaded] = useState(false);

  // Load comments from localStorage when expanded
  useEffect(() => {
    if (!expanded || loaded) return;
    const stored = findItems('comments', c => c.postId === postId);
    if (stored.length > 0) {
      setComments(stored);
    } else {
      // Fallback to mock comments when none stored yet
      setComments([
        { id: 'c1', userName: 'Michael Rivera', userAvatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=3', text: 'This is incredible work. The transparency is what keeps me donating every month.', createdAt: '2h ago', likes: 4 },
        { id: 'c2', userName: 'Jessica Okafor', userAvatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=7', text: 'Love seeing the verified impact data. Keep it up!', createdAt: '4h ago', likes: 7 },
        { id: 'c3', userName: 'David Park', userAvatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=12', text: 'Just shared this with my company. We want to sponsor next month!', createdAt: '6h ago', likes: 12 },
      ]);
    }
    setLoaded(true);
  }, [expanded, postId, loaded]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!input.trim()) return;
    const newComment = {
      id: `c-${Date.now()}`,
      userName: user?.name || 'You',
      userAvatar: `https://api.dicebear.com/7.x/avataaars/svg?seed=${user?.username || 'u'}`,
      text: input,
      createdAt: 'Just now',
      likes: 0,
    };
    setComments(prev => [...prev, newComment]);
    setInput('');
    if (onCommentAdd) onCommentAdd();

    // Persist to localStorage
    addItem('comments', {
      postId,
      userId: user?.username || 'anonymous',
      userName: newComment.userName,
      userAvatar: newComment.userAvatar,
      text: newComment.text,
      likes: 0,
    });
  };

  const toggleLike = (id) => setLikedComments(prev => ({ ...prev, [id]: !prev[id] }));

  if (!expanded) return null;

  const displayTime = (c) => {
    if (c.createdAt === 'Just now') return 'Just now';
    if (typeof c.createdAt === 'string' && c.createdAt.includes('T')) {
      const diff = Date.now() - new Date(c.createdAt).getTime();
      const mins = Math.floor(diff / 60000);
      if (mins < 60) return `${Math.max(1, mins)}m ago`;
      const hrs = Math.floor(mins / 60);
      if (hrs < 24) return `${hrs}h ago`;
      return `${Math.floor(hrs / 24)}d ago`;
    }
    return c.createdAt;
  };

  return (
    <div className="mt-3 pt-3 border-t border-slate-100" data-testid={`comments-${postId}`}>
      {comments.map(c => (
        <div key={c.id} className="flex gap-2.5 mb-3" data-testid={`comment-${c.id}`}>
          <img src={c.userAvatar || `https://api.dicebear.com/7.x/avataaars/svg?seed=${c.userName}`} alt="" className="w-7 h-7 rounded-full shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="bg-slate-50 rounded-lg px-3 py-2">
              <span className="text-xs font-semibold text-slate-900">{c.userName}</span>
              <p className="text-xs text-slate-600 mt-0.5">{c.text}</p>
            </div>
            <div className="flex items-center gap-3 mt-1 px-1">
              <span className="text-[10px] text-slate-400">{displayTime(c)}</span>
              <button onClick={() => toggleLike(c.id)} className={`text-[10px] font-medium ${likedComments[c.id] ? 'text-rose-500' : 'text-slate-400 hover:text-slate-600'}`}>
                Like{(c.likes || 0) + (likedComments[c.id] ? 1 : 0) > 0 ? ` (${(c.likes || 0) + (likedComments[c.id] ? 1 : 0)})` : ''}
              </button>
            </div>
          </div>
        </div>
      ))}

      <form onSubmit={handleSubmit} className="flex items-center gap-2">
        <div className="w-7 h-7 rounded-full bg-gradient-to-r from-[#7C3AED] to-[#2563EB] flex items-center justify-center text-white text-[10px] font-bold shrink-0">
          {user?.initials || '?'}
        </div>
        <div className="flex-1 relative">
          <input
            value={input}
            onChange={e => setInput(e.target.value)}
            placeholder="Write a comment..."
            className="w-full h-8 pl-3 pr-8 rounded-full bg-slate-100 text-xs text-slate-900 placeholder-slate-400 outline-none focus:ring-1 focus:ring-slate-300"
            data-testid={`comment-input-${postId}`}
          />
          <button type="submit" className="absolute right-2 top-1/2 -translate-y-1/2 text-[#7C3AED] disabled:text-slate-300" disabled={!input.trim()} data-testid={`comment-send-${postId}`}>
            <Send className="w-3.5 h-3.5" />
          </button>
        </div>
      </form>
    </div>
  );
}
